// Kiosk behaviours for the wall tablet:
//  - keep the screen awake (Wake Lock API, where available),
//  - return to Today after a period of inactivity,
//  - at night, fade to a dim clock after inactivity,
//  - by day, a slow photo screensaver after longer inactivity (it drifts,
//    so nothing sits still on the panel for hours),
//  - a gentle chime when a toilet trip becomes due,
//  - "screen off" (the power button in the top bar): a black screen, no
//    chimes, and the wake lock let go so Android's own screen timeout can
//    switch the panel off for real. Tap, or press the side button, to wake.

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useDevice, useHousehold, useLiveStatus } from '../data/hooks.ts';
import { inClockWindow } from '../domain/time.ts';
import { currentRoute, navigate } from './router.ts';

let screenOff = false;
const offListeners = new Set<() => void>();
/** Turn the kiosk screen off (true) or back on (false). */
export function setScreenOff(off: boolean): void {
  if (screenOff === off) return;
  screenOff = off;
  offListeners.forEach((l) => l());
}
export function useScreenOff(): boolean {
  return useSyncExternalStore(
    (l) => {
      offListeners.add(l);
      return () => offListeners.delete(l);
    },
    () => screenOff,
  );
}

export function useKiosk() {
  const off = useScreenOff();
  const offRef = useRef(off);
  offRef.current = off;
  const device = useDevice();
  const household = useHousehold();
  const status = useLiveStatus(30_000);
  const isNight = inClockWindow(status.now, household.nightStart, household.nightEnd);
  const [dimmed, setDimmed] = useState(false);
  const [ambient, setAmbient] = useState(false);
  const lastInput = useRef(Date.now());

  const wake = useCallback(() => {
    lastInput.current = Date.now();
    setDimmed(false);
    setAmbient(false);
    setScreenOff(false);
  }, []);

  // Track activity.
  useEffect(() => {
    const onInput = () => {
      lastInput.current = Date.now();
    };
    const opts = { passive: true, capture: true } as const;
    window.addEventListener('pointerdown', onInput, opts);
    window.addEventListener('keydown', onInput, opts);
    return () => {
      window.removeEventListener('pointerdown', onInput, opts);
      window.removeEventListener('keydown', onInput, opts);
    };
  }, []);

  // Idle checks.
  useEffect(() => {
    if (!device.kiosk) return;
    const t = setInterval(() => {
      const idle = (Date.now() - lastInput.current) / 1000;
      const route = currentRoute();
      // Never cover a dialog, the voice assistant or a camera being watched.
      const sheetOpen = !!document.querySelector('.sheet, .voice, .cam-capture');
      if (device.idleReturnSec && idle > device.idleReturnSec && route.path !== '/' && route.segments[0] !== 'emergency' && route.segments[0] !== 'setup' && !sheetOpen) navigate('/');
      if (device.nightDimSec && isNight && idle > device.nightDimSec && !sheetOpen) setDimmed(true);
      if (device.ambientSec && !isNight && idle > device.ambientSec && !sheetOpen && route.segments[0] !== 'emergency' && route.segments[0] !== 'camera') setAmbient(true);
    }, 5000);
    return () => clearInterval(t);
  }, [device.kiosk, device.idleReturnSec, device.nightDimSec, device.ambientSec, isNight]);

  useEffect(() => {
    if (!isNight) setDimmed(false);
    else setAmbient(false);
  }, [isNight]);

  // Coming back from a real screen-off (the side button) means someone wants it on.
  useEffect(() => {
    if (!device.kiosk) return;
    const onVis = () => {
      if (document.visibilityState === 'visible' && offRef.current) wake();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [device.kiosk, wake]);

  // Keep the screen on — except when it's been turned off, when the lock is let
  // go so the tablet's own screen timeout can switch the panel off.
  useEffect(() => {
    if (!device.kiosk || off || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | undefined;
    let live = true;
    const acquire = async () => {
      try {
        const l = await navigator.wakeLock.request('screen');
        if (live) lock = l;
        else void l.release().catch(() => undefined);
      } catch {
        /* not allowed (e.g. insecure context) — MDM keeps the screen on instead */
      }
    };
    void acquire();
    const onVis = () => document.visibilityState === 'visible' && void acquire();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      live = false;
      document.removeEventListener('visibilitychange', onVis);
      void lock?.release().catch(() => undefined);
    };
  }, [device.kiosk, off]);

  // Chime when a toilet trip becomes due (daytime only, kiosk only).
  const lastUrgency = useRef(status.toilet.urgency);
  useEffect(() => {
    const prev = lastUrgency.current;
    lastUrgency.current = status.toilet.urgency;
    if (!device.kiosk || !device.chime || isNight || offRef.current || !status.isHome || status.toilet.stale) return;
    if (status.toilet.urgency === 'now' && prev !== 'now' && prev !== 'overdue') chime();
  }, [status.toilet.urgency, device.kiosk, device.chime, isNight, status.isHome]);

  return { isNight, dimmed: dimmed && device.kiosk && !off, ambient: ambient && device.kiosk && !dimmed && !off, off: off && device.kiosk, wake };
}

let audio: AudioContext | undefined;

/** Soft two-note chime synthesised with Web Audio — no asset needed. */
export function chime() {
  try {
    audio ??= new AudioContext();
    const ctx = audio;
    const now = ctx.currentTime;
    [659.25, 880].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.22);
      gain.gain.exponentialRampToValueAtTime(0.25, now + i * 0.22 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.22 + 0.9);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.22);
      osc.stop(now + i * 0.22 + 1);
    });
  } catch {
    /* audio unavailable */
  }
}
